import React, { useEffect, useState, useCallback } from "react";
import { Button, message, Input } from "antd";
import {
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import AddStoreProductForm from "./StoresForm"; // Form component for add/edit
import API from "../../api"; // API instance
import CustomTable from "../../components/Table/CustomTable";
import ConfirmationModal from "../../components/ConfirmationModal"; // Import reusable confirmation modal

export default function AddStoresProduct() {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [storeToDelete, setStoreToDelete] = useState(null);
  const [paginationInfo, setPaginationInfo] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch stores whenever the component mounts or dependencies change
  const getStores = async () => {
    try {
      setLoading(true);
      const { current, pageSize } = paginationInfo;
      const response = await API.get("/stores/paginate", {
        params: { page: current, limit: pageSize, search: searchQuery },
      });
      const { stores: getStores, pagination } = response.data;
      setStores(getStores);
      setPaginationInfo((prev) => ({
        ...prev,
        total: pagination?.totalCount,
      }));
    } catch (err) {
      console.error("Error fetching stores:", err);
      message.error("Failed to fetch stores. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStores();
  }, [paginationInfo.current, paginationInfo.pageSize, searchQuery]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPaginationInfo((prev) => ({ ...prev, current: 1 }));
  };

  const handleAddOrEditStore = async (storeData) => {
    try {
      if (isEdit) {
        // Update Store
        const res = await API.put(`/stores/${selectedStore._id}`, storeData);
        if (res.status === 200) {
          message.success("Store updated successfully!");
        }
      } else {
        // Add new Store
        const res = await API.post("/stores", storeData);
        if (res.status === 200) {
          message.success("Store added successfully!");
        }
      }
      getStores(); // Refresh the Store list
      setOpenDrawer(false);
    } catch (error) {
      console.error("Error saving Store:", error);
      message.error(error.response.data.message);
    }
  };

  // Function to handle store deletion
  const handleDeleteStore = useCallback(async () => {
    try {
      await API.delete(`/stores/${storeToDelete._id}`);
      setStores((prev) =>
        prev.filter((store) => store._id !== storeToDelete._id)
      );
      setIsDeleteModalVisible(false); // Close the modal after deletion
      message.success("Store deleted successfully!");
    } catch (err) {
      console.error("Error deleting store:", err);
      message.error(err.response.data.message);
    }
  }, [storeToDelete]);

  // Function to set up editing mode or open form with pre-selected store
  const handleAdd = (store) => {
    setIsEdit(false); // Not editing a store, just adding stock for a store
    setSelectedStore(store); // Pass the selected store
    setOpenDrawer(true);
  };

  // Function to show the delete confirmation modal
  const showDeleteModal = (store) => {
    setStoreToDelete(store);
    setIsDeleteModalVisible(true);
  };

  const columns = [
    {
      title: "Index",
      key: "index",
      render: (_, __, index) =>
        (paginationInfo.current - 1) * paginationInfo.pageSize + index + 1,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (record) => (
        <div>
          <Button
            icon={<PlusOutlined />}
            onClick={() => handleAdd(record)}
            style={{ marginRight: 8 }}
          />
          {/* <Button
            icon={<DeleteOutlined />}
            onClick={() => showDeleteModal(record)} // Show delete confirmation
            danger
          /> */}
        </div>
      ),
    },
  ];

  return (
    <div
      style={{
        padding: "20px",
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      }}
    >
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setIsEdit(false);
            setSelectedStore(null);
            setOpenDrawer(true);
          }}
        >
          Stock Add
        </Button>
        <Input
          placeholder="Search Store"
          prefix={<SearchOutlined />}
          value={searchQuery}
          onChange={handleSearch}
          style={{ width: 300 }}
        />
      </div>

      {openDrawer && (
        <AddStoreProductForm
          open={openDrawer}
          setOpen={setOpenDrawer}
          data={selectedStore} // Pass the selected store as data
        />
      )}

      <CustomTable
        columns={columns}
        dataSource={stores}
        onPaginationChange={(pagination) => {
          setPaginationInfo({
            current: pagination.current,
            pageSize: pagination.pageSize,
          });
        }}
        loading={loading}
        style={{ padding: "0 20px" }}
        total={paginationInfo.total}
      />

      <ConfirmationModal
        visible={isDeleteModalVisible}
        onConfirm={handleDeleteStore}
        onCancel={() => setIsDeleteModalVisible(false)}
        title="Confirm Deletion"
        content="Are you sure you want to delete this store? This action cannot be undone."
      />
    </div>
  );
}
